import { Button, Card, EmptyState, Section, StatusPill, useToast } from "@bafe/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { ConfirmDialog } from "../../components/ConfirmDialog";
import { ErrorPanel } from "../../components/ErrorPanel";
import { QueryState } from "../../components/QueryState";
import { api } from "../../lib/api";
import { formatDateTime, formatRelative } from "../../lib/format";
import { queryKeys } from "../../lib/query";

export function SessionsSection() {
	const queryClient = useQueryClient();
	const { toast } = useToast();
	const [confirmOthers, setConfirmOthers] = useState(false);

	const sessions = useQuery({ queryKey: queryKeys.sessions, queryFn: () => api.listSessions() });

	const revoke = useMutation({
		mutationFn: (id: string) => api.revokeSession(id),
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: queryKeys.sessions });
			toast({ tone: "success", message: "That device is signed out." });
		},
	});

	const revokeOthers = useMutation({
		mutationFn: () => api.revokeOtherSessions(),
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: queryKeys.sessions });
			setConfirmOthers(false);
			toast({ tone: "success", message: "Every other device is signed out." });
		},
	});

	return (
		<Section
			title="Where you are signed in"
			description="Each entry is a browser or device with an active Key session."
			action={
				<Button variant="secondary" onClick={() => setConfirmOthers(true)}>
					Sign out other devices
				</Button>
			}
		>
			{revoke.isError && <ErrorPanel error={revoke.error} title="We could not sign that device out." />}

			<QueryState query={sessions} errorTitle="We could not load your sessions.">
				{(list) => (
					<Card flush>
						{list.length === 0 ? (
							<EmptyState
								title="No other sessions"
								description="You are only signed in here."
							/>
						) : (
							<ul>
								{list.map((device) => (
									<li
										key={device.id}
										className="flex flex-wrap items-center justify-between gap-3 border-b border-(--border-subtle) px-4 py-3.5 last:border-0"
									>
										<div className="flex min-w-0 flex-col gap-0.5">
											<div className="flex flex-wrap items-center gap-2">
												<p className="text-sm font-medium text-(--text-primary)">
													{device.label}
												</p>
												{device.current && <StatusPill tone="info">This device</StatusPill>}
											</div>
											<p className="text-sm text-(--text-secondary)">
												Last used {formatRelative(device.lastSeenAt)}
												{device.ipAddress ? ` · ${device.ipAddress}` : ""}
											</p>
											<p className="text-xs text-(--text-muted)">
												Signed in {formatDateTime(device.createdAt)}
											</p>
										</div>

										{!device.current && (
											<Button
												size="sm"
												variant="ghost"
												pending={revoke.isPending && revoke.variables === device.id}
												pendingLabel="Signing out…"
												onClick={() => revoke.mutate(device.id)}
											>
												Sign out<span className="sr-only"> {device.label}</span>
											</Button>
										)}
									</li>
								))}
							</ul>
						)}
					</Card>
				)}
			</QueryState>

			<ConfirmDialog
				open={confirmOthers}
				onClose={() => setConfirmOthers(false)}
				title="Sign out every other device?"
				description="You stay signed in here. Everywhere else will need your password again. Mailbox passwords are not affected."
				confirmLabel="Sign out other devices"
				pending={revokeOthers.isPending}
				error={revokeOthers.error}
				onConfirm={() => revokeOthers.mutate()}
			/>
		</Section>
	);
}
